import React, { useContext, useEffect, useRef, useState } from 'react';
import { TRACK_INTERACTION, triggerEvent } from '@fec/assets/js/utils/event';
import AlgoliaResult from '@fec/frontend/elements/search/algolia-result/algolia-result';
import NoResults from './NoResults';
import { getTrackingInformation, translate } from '../searchHelpers';
import AlgoliaClient from '../search/AlgoliaClient';
import {
  ACTION_NEXT_PAGE,
  searchContextStore,
} from '../search/SearchContextProvider';
import ShowMoreBar from '@fec/frontend/modules/groups-and-lists/show-more-bar/show-more-bar';

const BACK_TO_TOP_TRESHOLD = 5; // how many items to show until backtotop is visible.

export const AlgoliaSearchContainer = (props) => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [numberOfHits, setNumberOfHits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { state: searchContext, dispatch } = useContext(searchContextStore);

  const pageBreakRef = useRef();
  const loadMoreRef = useRef();

  useEffect(() => {
    setFocus();
  }, [items]);

  const searchClient = new AlgoliaClient(
    props.algoliaIndex,
    searchContext.translations,
    props.isWebview,
  );

  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );

  useEffect(() => {
    search();
  }, [searchContext.searchParams]);

  const trackingSource = getTrackingInformation(
    searchContext.searchConfig.portalName,
    [],
    [],
  ).source;

  /* updates the page search param in context (which triggers the load more event we are listening to above) */
  const loadMoreClickHandler = () => {
    if (!isLoading) {
      triggerEvent(TRACK_INTERACTION, {
        event_source: trackingSource,
        event_name: searchContext.searchParams.q,
        event_value: 'load_more-' + (searchContext.searchParams.page * 1 + 1),
        event_type: 'hidden_event',
      });

      // increment page in context
      dispatch({ type: ACTION_NEXT_PAGE });
    }
  };

  /*
   * Search is triggered by context changes and relies solely on the current context for filtering / paging.
   */
  const search = () => {
    if (searchContext.searchParams.q === '') {
      // Reset search results if search string is reset
      return;
    }

    setIsLoading(true);
    setNumberOfHits(0);

    return searchClient
      .getArticles(
        searchContext.searchParams.q,
        searchClient.getArticleSearchOptions(
          0,
          searchContext.searchConfig.algoliaSearch.hitsPerPage *
            (searchContext.searchParams.page * 1 + 1), // fetch last page + 1
          getFiltersFromConfig(),
        ),
      )
      .then((results) => {
        showResults(results);
        setIsLoading(false);
      });
  };

  /**
   * Gets the filters from the config file and sets their values.
   * Takes also invisible filters into account. (e.g. portal, rubric are invisible filters).
   * @returns {Array}
   */
  const getFiltersFromConfig = () => {
    let config = searchContext.searchConfig;
    let filters = [];
    config.filters.forEach((filter) => {
      let selectedOption = filter.hidden
        ? filter.selectedOption
        : searchContext.searchParams[filter.name];
      if (selectedOption && selectedOption !== 'all') {
        filters.push({ name: filter.name, option: selectedOption });
      }
    });
    return filters;
  };

  const showResults = (algoliaResponse) => {
    const hasMoreResults = algoliaResponse.hasMoreResults();
    const numberOfHits = algoliaResponse.getCount();

    let items = algoliaResponse.getItems();

    if (items.length > 0) {
      const firstNewItemIndex =
        searchContext.searchParams.page *
        searchContext.searchConfig.algoliaSearch.hitsPerPage;
      items[firstNewItemIndex].pageBreak = true;
    }

    setHasMoreResults(hasMoreResults);
    setNumberOfHits(numberOfHits);
    setSearchQuery(searchContext.searchParams.q);
    setItems(items);
  };

  const setFocus = () => {
    // remove the element from the natural tabindex order and add it after setting the focus again.
    // This ensures that the tab order stays the same as before.
    // If tabindex is not set, the next element which gets focus will be a footer element.
    if (searchContext.searchParams.page >= 1) {
      // on the last page, loadMore ref is gone.
      if (pageBreakRef && pageBreakRef.current) {
        if (loadMoreRef.current) {
          loadMoreRef.current.tabIndex = -1;
        }
        pageBreakRef.current.focus();
        if (loadMoreRef.current) {
          loadMoreRef.current.tabIndex = 0;
        }
      }
    }
  };

  // display nothing on initial load / while fetching results
  if (searchQuery === '') {
    return null;
  }

  /*
    Backtotop Button visibility depends on list length (must be >= 5 results). backtotop is always visible (unless specified in config), loadmore not.
    To ease the rendering, we set the visibility (to keep their position) or dont render them.
    All combinations of hasMore & ItemLength:
     - 1. hasMoreResults true && items.length >= 5:     hasMore visible, backtotop visible
     - 2. hasMoreResults false && items.length > 0 < 5: hasMore hidden, backtotop hidden -> DON'T RENDER BOTH
     - 3. hasMoreResults false && items.length >= 5:    hasMore hidden, backtotop visible
     - (4. hasMoreResults true && items.length < 5:     not a usecase (assuming page size is >= 5))
     => its enough to render/don't render them both and set the visibility only on hasMore.
  */

  // case 2 (inversed: decide to render the buttons)
  let hasMoreOrBackToTopButtons =
    hasMoreResults || items.length >= BACK_TO_TOP_TRESHOLD;
  let renderBackToTop = !searchContext.searchConfig.algoliaSearch.hideBackToTop;

  const referrerTrack =
    'srg_override_mod1=cms_result' +
    '&srg_override_mod2=' +
    trackingSource +
    '&srg_override_mod3=article';

  return (
    <div
      className="collection-ng js-collection-ng collection-ng--list"
      aria-live="polite">
      <div className="collection-ng__title-wrapper">
        <div className="collection-ng__title-item collection-ng__title-item--grower">
          <h3 className="collection-ng__heading">
            <span className="collection-ng__heading-em">{searchQuery}</span>
            {searchContext.searchConfig.portalName === 'all' // global search
              ? _t('inArticle')
              : _t('inRubric').replace(
                  '%rubric%',
                  searchContext.searchConfig.portalName,
                )}{' '}
            ({numberOfHits.toLocaleString(searchContext.searchConfig.locale)})
          </h3>
        </div>
      </div>

      {items.length > 0 && (
        <ul
          className="collection-ng__teaser-list js-teaser-ng-list"
          data-referrer-track={referrerTrack}>
          {items.map((result) => (
            <AlgoliaResult
              ref={pageBreakRef}
              result={result}
              key={result.urn}
            />
          ))}
        </ul>
      )}
      {items.length === 0 && <NoResults />}

      {hasMoreOrBackToTopButtons && (
        <ShowMoreBar
          styleModifier="show-more-bar--algolia-search"
          hasMore={hasMoreResults}
          showMoreText={_t('showMoreResults')}
          onClick={loadMoreClickHandler}
          loadMoreButtonDisabled={isLoading}
          loadMoreButtonRef={loadMoreRef}
          renderBackToTop={renderBackToTop}
          backToTopLink="#top"
          backToTopText={_t('backtotop')}
        />
      )}
    </div>
  );
};
